import { AppDispatch } from '@/app/store';
import { setLanguage, markLanguageInitialized } from '@/app/store/slices/languageSlice';
import { fetchUserLanguagePreference } from '@/app/store/thunks/languageThunks';
import {
  detectBrowserLanguage,
  loadGuestLanguagePreference,
  updateHtmlAttributes,
  clearGuestLanguagePreference,
  saveGuestLanguagePreference,
  isValidLanguageCode
} from './languageUtils';
import { LanguageCode } from '@/app/store/slices/languageSlice';

/**
 * Initialize language on app load
 * Priority order:
 * 1. Authenticated user: Fetch from backend
 * 2. Guest user: Load from local storage
 * 3. New user: Detect browser language
 * 4. Fallback: Use default 'en'
 */
export const initializeLanguage = async (
  dispatch: AppDispatch,
  userId?: number,
  isAuthenticated?: boolean
): Promise<void> => {
  try {
    console.log('Initializing language...', { userId, isAuthenticated });

    // Case 1: Authenticated user - fetch from backend
    if (isAuthenticated && userId) {
      console.log('Fetching language preference for authenticated user:', userId);
      
      try {
        // Session management is now handled by Redux and consolidated services
        // The sessionManager service has been removed as part of consolidation
        
        await dispatch(fetchUserLanguagePreference(userId));
        
        // Get the language from Redux state after fetch
        const state = (dispatch as any).getState?.();
        const languageCode = state?.language?.currentLanguage || 'en';
        
        console.log('Fetched language preference:', languageCode);
        
        // Update HTML attributes
        updateHtmlAttributes(languageCode);

        // Persist the resolved language to localStorage as a "last known
        // language" cache — even for authenticated users. A later token-expired
        // reload hits the guest fallback (LanguageProvider's 2s timer) BEFORE
        // auth re-settles; if this cache were cleared here, that fallback would
        // drop to browser detection and the landing hero would start typing the
        // tagline in the wrong locale (and at the wrong CJK/Latin speed) before
        // the real preference loads. Keeping it in sync is harmless — load-time
        // validation rejects anything unsupported. (Pre-login guest→DB migration
        // still clears it via migrateGuestLanguagePreference.)
        if (isValidLanguageCode(languageCode)) {
          saveGuestLanguagePreference(languageCode);
        }

        // Mark as initialized
        dispatch(markLanguageInitialized());

        return;
      } catch (error) {
        console.error('Failed to fetch user language preference:', error);
        // Continue to fallback logic
      }
    }

    // Case 2: Guest user - load from local storage
    const guestLanguage = loadGuestLanguagePreference();
    if (guestLanguage) {
      console.log('Loaded guest language preference:', guestLanguage);
      
      dispatch(setLanguage(guestLanguage));
      updateHtmlAttributes(guestLanguage);
      dispatch(markLanguageInitialized());
      
      return;
    }

    // Case 3: New user - detect browser language
    const browserLanguage = detectBrowserLanguage();
    console.log('Detected browser language:', browserLanguage);
    
    dispatch(setLanguage(browserLanguage));
    updateHtmlAttributes(browserLanguage);
    
    // Save to local storage for guest users
    if (!isAuthenticated) {
      saveGuestLanguagePreference(browserLanguage);
    }
    
    dispatch(markLanguageInitialized());
    
  } catch (error) {
    console.error('Language initialization failed:', error);
    
    // Fallback to default language
    const defaultLanguage: LanguageCode = 'en-US';
    dispatch(setLanguage(defaultLanguage));
    updateHtmlAttributes(defaultLanguage);
    dispatch(markLanguageInitialized());
  }
};

/**
 * Migrate guest language preference to database after login
 * Called after successful authentication
 */
export const migrateGuestLanguagePreference = async (
  dispatch: AppDispatch,
  userId: number
): Promise<void> => {
  try {
    const guestLanguage = loadGuestLanguagePreference();
    
    if (guestLanguage) {
      console.log('Migrating guest language preference to database:', guestLanguage);
      
      // Import the update thunk
      const { updateUserLanguagePreference } = await import('@/app/store/thunks/languageThunks');
      
      // Update in database (partitionKey defaults to PARTITION_KEY)
      await dispatch(updateUserLanguagePreference(userId, guestLanguage));
      
      // Clear local storage after successful migration
      clearGuestLanguagePreference();
      
      console.log('Guest language preference migrated successfully');
    }
  } catch (error) {
    console.error('Failed to migrate guest language preference:', error);
    // Don't throw - this is not critical
  }
};
